import { useState, useEffect, useRef, useMemo, useCallback, type ReactNode } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, X, Loader2, Check, Info, ChevronDown } from "lucide-react";
import { ethers } from "ethers";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import AmountPercentSlider from "./AmountPercentSlider";
import TokenIcon from "../TokenIcon";
import { SUPPORTED_TOKENS, MINIMUM_PORTAL_IN_AMOUNTS, CONTRACT_ADDRESSES, ERC20_ABI, getPublicTokensForChain, BRIDGE_ABI, BRIDGE_ERC20_ABI } from "@/contracts/config";
import { getChainConfig, getRpcUrlForChain } from "@/chains";
import { useTokenPrices, PORTAL_IN_MAX_USD } from "@/hooks/useTokenPrices";
import {
  DUST_AMOUNT_THRESHOLD,
  formatPlainDecimal,
  truncateDecimalValue,
} from "@/lib/utils";
import { resolveTokenIcon } from "@/lib/tokenIcons";
import PodRequestTracker from "@/components/PodRequestTracker";
import type { PodPortalRequest } from "@/contracts/pod";
import { useAccount } from "wagmi";
import { resolveConnectedProvider } from "@coti-io/coti-wallet-plugin";
import { POD_PORTAL_ADMIN_ABI } from "@/contracts/pod";
import { useBodyScrollLock } from "@/hooks/useBodyScrollLock";

type StepStatus = "pending" | "loading" | "active" | "completed" | "error";

const TX_HASH_PATTERN = /\b(0x[a-fA-F0-9]{64})\b/;
const POD_PENDING_REQUEST_PATTERN =
  /PoD request is already pending[^\n]*?\(request (0x[a-fA-F0-9]{64})\)/i;

function extractTxHashFromError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const e = err as {
    txHash?: string;
    hash?: string;
    receipt?: { hash?: string };
    transaction?: { hash?: string };
    message?: string;
    explorerUrl?: string;
    podRequestId?: string;
  };
  // Pending PoD request IDs are bytes32, not chain tx hashes — don't treat them as Failed TX.
  if (e.explorerUrl || e.podRequestId || POD_PENDING_REQUEST_PATTERN.test(e.message ?? "")) {
    return null;
  }
  const direct = e.txHash || e.hash || e.receipt?.hash || e.transaction?.hash;
  if (direct) return direct;

  const msg = e.message ?? "";
  const labeled = msg.match(/\bTX:\s*(0x[a-fA-F0-9]{64})\b/);
  if (labeled) return labeled[1];

  const embedded = msg.match(TX_HASH_PATTERN);
  return embedded ? embedded[1] : null;
}

function extractPodExplorerUrlFromError(err: unknown): string | null {
  if (!err || typeof err !== "object") return null;
  const e = err as { explorerUrl?: string; message?: string; podRequestId?: string };
  // Pending PoD request IDs are not chain txs — always send users to the PoD explorer.
  if (e.podRequestId || POD_PENDING_REQUEST_PATTERN.test(e.message ?? "")) {
    return "https://explorer.pod.coti.io/";
  }
  if (typeof e.explorerUrl === "string" && /^https?:\/\//i.test(e.explorerUrl)) {
    return e.explorerUrl;
  }
  return null;
}

function isInsufficientFundsError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { code?: unknown; message?: string; info?: { error?: { message?: string } } };
  if (e.code === "INSUFFICIENT_FUNDS") return true;
  const msg = `${e.message ?? ""} ${e.info?.error?.message ?? ""}`.toLowerCase();
  return msg.includes("insufficient funds") || msg.includes("insufficient balance for transfer");
}

function isRateLimitedError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const e = err as { message?: string; info?: { error?: { message?: string } } };
  const msg = `${e.message ?? ""} ${e.info?.error?.message ?? ""}`.toLowerCase();
  return msg.includes("rate limited") || msg.includes("rate limit") || msg.includes("too many requests");
}

function formatActionError(
  err: any,
  options: { fallback: string; rejected: string; feeToken: string },
): string {
  if (err?.code === 4001 || err?.message?.includes("rejected")) {
    return options.rejected;
  }
  if (isInsufficientFundsError(err)) {
    return `Insufficient ${options.feeToken} to pay the network fee. Add ${options.feeToken} to your wallet and try again.`;
  }
  if (isRateLimitedError(err)) {
    return "The network RPC is rate limited. Please reload the page and try again.";
  }
  if (err?.message) return err.message;
  return options.fallback;
}

interface TokenOption {
  publicSymbol: string;
  publicName: string;
  privateSymbol: string;
  icon: string;
  publicBalance: string;
  privateBalance: string;
}

interface AmountInputModalProps {
  isOpen: boolean;
  /** When false, the modal will not render even if isOpen is true. */
  isPrivateUnlocked?: boolean;
  onClose: () => void;
  amount: string;
  onAmountChange: (amount: string) => void;
  maxAmount: number;
  tokenSymbol: string;
  direction: "in" | "out";
  onConfirm: (
    onProgress?: (stage: "approve-start" | "approve-complete" | "transfer-start" | "transfer-complete", txHash?: string) => void,
    /** When set (COTI Portal In gross-up), bridge this amount instead of the typed receive amount. */
    overrideAmount?: string,
  ) => Promise<void>;
  explorerBaseUrl?: string;
  isProcessing: boolean;
  isApprovalNeeded?: boolean;
  onApprove?: () => Promise<void> | void;
  isApproving?: boolean;
  estimatedGasFee?: string | null;
  isGasEstimating?: boolean;
  portalFeeCoti?: string | null;
  /** PoD: native-denominated portal fee. */
  portalFee?: string | null;
  /** PoD: native symbol for portal fee (ETH, AVAX). */
  portalFeeSymbol?: string;
  /** PoD: inbox fee paid via msg.value. */
  podInboxFee?: string | null;
  /** PoD: L1 execution gas (wallet-paid). */
  l1GasFee?: string | null;
  /** True when chain uses pod-privacy-portal strategy. */
  isPodChain?: boolean;
  feeDebugInfo?: { cotiLastUpdated: string; tokenLastUpdated: string; blockTimestamp: string } | null;
  isBridgePaused?: boolean;
  /** COTI bridges: false blocks Portal In only (withdrawals still allowed). */
  isDepositEnabled?: boolean;
  /** On-chain portal/bridge limits (token units). Combined with USD min/max in the UI. */
  minDepositAmount?: string | null;
  maxDepositAmount?: string | null;
  minWithdrawAmount?: string | null;
  maxWithdrawAmount?: string | null;
  /** Symbol for PoD fee lines (default: COTI). Use `native` for Sepolia/Fuji. */
  estimatedGasSymbol?: string;
  /** Optional: Token pairs for dropdown selector (when called from ActionBar Portal In/Out) */
  tokenPairs?: TokenOption[];
  /** Optional: Callback when token is selected from dropdown */
  onTokenSelect?: (token: TokenOption) => void;
  /** Cached COTI balance from context — used as fallback if on-chain fetch fails */
  fallbackCotiBalance?: string | null;
  /** The currently connected wallet address — used to read balances without triggering wallet popups */
  connectedAddress?: string | null;
  /** Show inline PoD request tracker during step-flow (Sepolia / Fuji). */
  showPodRequestTracker?: boolean;
  /** Live PoD requests from privacy bridge context. */
  podRequests?: PodPortalRequest[];
  /** Source chain id for PoD tracking labels. */
  podChainId?: number;
}

// ─── Step Circle ────────────────────────────────────────────────────────────
const StepCircle = ({
  stepNum,
  status,
  label,
  labelAction,
  onClick,
}: {
  stepNum: number;
  status: StepStatus;
  label: string;
  labelAction?: ReactNode;
  onClick?: () => void;
}) => {
  const isCompleted = status === "completed";
  const isActive = status === "active" || status === "loading";
  const isError = status === "error";

  const circleClass = `
    w-12 h-12 rounded-full flex items-center justify-center relative transition-all duration-300
    ${isCompleted ? "bg-purple-600" : ""}
    ${isActive ? "bg-transparent border-2 border-purple-500 shadow-[0_0_18px_rgba(139,92,246,0.55)]" : ""}
    ${!isCompleted && !isActive && !isError ? "bg-gray-300 dark:bg-muted" : ""}
    ${isError ? "bg-red-500/20 border-2 border-red-500" : ""}
    ${onClick ? "cursor-pointer hover:opacity-80" : ""}
  `;

  return (
    <div className="flex flex-col items-center gap-2">
      {onClick ? (
        <button type="button" onClick={onClick} className={circleClass} aria-label={`${label} details`}>
          {isCompleted ? (
            <Check className="w-5 h-5 text-foreground" strokeWidth={2.5} />
          ) : status === "loading" ? (
            <Loader2 className="w-5 h-5 text-primary dark:text-purple-300 animate-spin" />
          ) : isError ? (
            <X className="w-5 h-5 text-red-400" />
          ) : (
            <span
              className={`text-sm font-semibold ${
                isActive ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {stepNum}
            </span>
          )}
        </button>
      ) : (
        <div className={circleClass}>
          {isCompleted ? (
            <Check className="w-5 h-5 text-foreground" strokeWidth={2.5} />
          ) : status === "loading" ? (
            <Loader2 className="w-5 h-5 text-primary dark:text-purple-300 animate-spin" />
          ) : isError ? (
            <X className="w-5 h-5 text-red-400" />
          ) : (
            <span
              className={`text-sm font-semibold ${
                isActive ? "text-foreground" : "text-muted-foreground"
              }`}
            >
              {stepNum}
            </span>
          )}
        </div>
      )}
      <div className="flex items-start justify-center gap-1 sm:items-center">
        <span
          className={`text-center text-xs font-medium leading-4 tracking-wide ${
            isActive || isCompleted ? "text-foreground" : "text-muted-foreground"
          }`}
        >
          {label}
        </span>
        {labelAction}
      </div>
    </div>
  );
};

// ─── Step Connector ──────────────────────────────────────────────────────────
const StepConnector = ({ active }: { active: boolean }) => (
  <div
    className={`flex-1 h-px mx-3 mb-5 transition-colors duration-300 ${
      active ? "bg-purple-500" : "bg-muted"
    }`}
  />
);

const AmountInputModal = ({
  isOpen,
  isPrivateUnlocked = true,
  onClose,
  amount,
  onAmountChange,
  maxAmount,
  tokenSymbol,
  direction,
  onConfirm,
  isProcessing,
  isApprovalNeeded = false,
  onApprove,
  isApproving = false,
  estimatedGasFee,
  isGasEstimating = false,
  portalFeeCoti,
  portalFee,
  portalFeeSymbol = "COTI",
  podInboxFee,
  l1GasFee,
  isPodChain: isPodChainFromContext = false,
  feeDebugInfo,
  explorerBaseUrl = "https://testnet.cotiscan.io",
  isBridgePaused = false,
  isDepositEnabled = true,
  minDepositAmount = null,
  maxDepositAmount = null,
  minWithdrawAmount = null,
  maxWithdrawAmount = null,
  estimatedGasSymbol = "COTI",
  tokenPairs,
  onTokenSelect,
  fallbackCotiBalance,
  connectedAddress,
  showPodRequestTracker = false,
  podRequests = [],
  podChainId,
}: AmountInputModalProps) => {
  const visible = isOpen && isPrivateUnlocked;
  useBodyScrollLock(visible);
  const { amountsForOneDollar, amountsForMaxUsd } = useTokenPrices();
  // RainbowKit/wagmi connector — never use window.ethereum for chain reads;
  // another installed extension may own it (e.g. report eth_chainId 0x1).
  const { connector } = useAccount();

  /** PoD chains (Fuji/Sepolia) — fallback when plugin context predates `isPodChain`. */
  const isPodChain = isPodChainFromContext || estimatedGasSymbol !== "COTI";

  const isGoingPrivate = direction === "in";
  const depositsBlocked = isGoingPrivate && !isDepositEnabled;
  
  // Token dropdown state (only used when tokenPairs is provided)
  const hasActionBarTokenPicker = !!(tokenPairs && tokenPairs.length > 0);
  const [showTokenDropdown, setShowTokenDropdown] = useState(false);
  const [selectedToken, setSelectedToken] = useState<TokenOption | null>(null);
  const [tokenPickConfirmed, setTokenPickConfirmed] = useState(false);

  // Initialize selected token when modal opens with tokenPairs
  useEffect(() => {
    if (isOpen && tokenPairs && tokenPairs.length > 0) {
      // Find token matching current tokenSymbol, or default to first
      const matchingToken = tokenPairs.find(t =>
        (isGoingPrivate ? t.publicSymbol : t.privateSymbol) === tokenSymbol
      );
      setSelectedToken(matchingToken || tokenPairs[0]);
    }
  }, [isOpen, tokenPairs, tokenSymbol, isGoingPrivate]);

  // Balance shown in the token picker: public (ERC20) for Portal In, private for Portal Out
  const getPickerBalance = (token: TokenOption) =>
    isGoingPrivate ? token.publicBalance : token.privateBalance;

  const formatBalanceMinDecimals = (balance: string, minDecimals = 2) => {
    const [integerPart, decimalPart = ""] = balance.split(".");
    return `${integerPart}.${decimalPart.padEnd(minDecimals, "0")}`;
  };

  // Handle token selection from dropdown
  const handleSelectToken = (token: TokenOption) => {
    setSelectedToken(token);
    setShowTokenDropdown(false);
    if (onTokenSelect) {
      onTokenSelect(token);
    }
  };

  // Track when fees are being recalculated after amount change
  const [feeStale, setFeeStale] = useState(false);
  const prevAmount = useRef(amount);
  /** When true, keep the amount synced to effective max as fees settle (Max click). */
  const maxPinnedRef = useRef(false);
  /** COTI Portal In: amount actually sent as msg.value (desired receive + portal fee). */
  const pendingSendAmountRef = useRef<string | null>(null);

  // When amount changes, mark fee as stale immediately
  useEffect(() => {
    if (prevAmount.current !== amount) {
      prevAmount.current = amount;
      // Only mark stale if there's a nonzero amount to estimate
      if (parseFloat(amount) > 0) {
        setFeeStale(true);
      } else {
        setFeeStale(false);
      }
    }
  }, [amount]);

  // When fee quotes update, they're fresh again
  useEffect(() => {
    setFeeStale(false);
  }, [portalFeeCoti, portalFee, podInboxFee, l1GasFee, estimatedGasFee]);

  const [availableCotiBalance, setAvailableCotiBalance] = useState<number | null>(null);
  const [nativeBalanceSnapshot, setNativeBalanceSnapshot] = useState<{
    chainId: number;
    address: string;
    balance: number;
  } | null>(null);
  const [cotiBalanceSource, setCotiBalanceSource] = useState<"none" | "chain" | "fallback">("none");
  const availableNativeBalance =
    nativeBalanceSnapshot &&
    connectedAddress &&
    (!podChainId || nativeBalanceSnapshot.chainId === podChainId) &&
    nativeBalanceSnapshot.address.toLowerCase() === connectedAddress.toLowerCase()
      ? nativeBalanceSnapshot.balance
      : null;

  const parseCotiBalance = useCallback((value: string | null | undefined) => {
    if (value == null || value === "") return null;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : null;
  }, []);

  /** Prefer the portal's connected chain; fall back to the RainbowKit provider. */
  const resolveActiveChainId = useCallback(async (): Promise<number | undefined> => {
    if (podChainId) return podChainId;

    try {
      const eip1193 = await resolveConnectedProvider(connector);
      const chainIdHex = await eip1193?.request?.({ method: "eth_chainId" });
      if (chainIdHex) return Number(chainIdHex);
    } catch {
      /* fall through */
    }
    return undefined;
  }, [podChainId, connector]);

  const fetchCotiBalance = useCallback(async () => {
    const fallback = parseCotiBalance(fallbackCotiBalance);

    // Guard: only proceed if we have a confirmed connected address.
    // This prevents triggering wallet popups from getSigner/listAccounts.
    if (!connectedAddress) {
      console.warn("[AmountInputModal][CotiBalance] No connectedAddress provided", { fallback });
      setAvailableCotiBalance(fallback);
      setCotiBalanceSource(fallback != null ? "fallback" : "none");
      return;
    }

    try {
      const chainId = await resolveActiveChainId();

      if (!chainId) {
        console.warn("[AmountInputModal][CotiBalance] Could not determine chainId", { fallback });
        setAvailableCotiBalance(fallback);
        setCotiBalanceSource(fallback != null ? "fallback" : "none");
        return;
      }

      // Use a read-only JSON-RPC provider — no wallet interaction required
      const readProvider = new ethers.JsonRpcProvider(getRpcUrlForChain(chainId), chainId);
      const account = connectedAddress;

      const cotiConfig = getPublicTokensForChain(chainId).find((token) => token.symbol === "COTI");
      if (!cotiConfig) {
        console.log("[AmountInputModal][CotiBalance] No COTI token on chain", { chainId, fallback });
        setAvailableCotiBalance(fallback ?? 0);
        setCotiBalanceSource(fallback != null ? "fallback" : "chain");
        return;
      }

      let balance = "0";
      if (!cotiConfig.addressKey || cotiConfig.isNative) {
        const nativeBalanceWei = await readProvider.getBalance(account);
        balance = ethers.formatEther(nativeBalanceWei);
      } else {
        const tokenAddress = CONTRACT_ADDRESSES[chainId]?.[cotiConfig.addressKey];
        if (tokenAddress) {
          const contract = new ethers.Contract(tokenAddress, ERC20_ABI, readProvider);
          const bal = await contract.balanceOf(account);
          balance = ethers.formatUnits(bal, cotiConfig.decimals);
        }
      }

      const parsed = parseFloat(balance) || 0;
      console.log("[AmountInputModal][CotiBalance] Fetched on-chain COTI balance", {
        chainId,
        account,
        balance: parsed,
        fallback,
      });
      setAvailableCotiBalance(parsed);
      setCotiBalanceSource("chain");
    } catch (error) {
      console.error("[AmountInputModal][CotiBalance] Fetch failed, using fallback if available", {
        error,
        fallback,
      });
      setAvailableCotiBalance(fallback);
      setCotiBalanceSource(fallback != null ? "fallback" : "none");
    }
  }, [fallbackCotiBalance, parseCotiBalance, connectedAddress, resolveActiveChainId]);

  const fetchNativeBalance = useCallback(async () => {
    if (!connectedAddress) {
      setNativeBalanceSnapshot(null);
      return;
    }

    try {
      const chainId = await resolveActiveChainId();
      if (!chainId) {
        setNativeBalanceSnapshot(null);
        return;
      }

      const readProvider = new ethers.JsonRpcProvider(getRpcUrlForChain(chainId), chainId);
      const nativeBalanceWei = await readProvider.getBalance(connectedAddress);
      setNativeBalanceSnapshot({
        chainId,
        address: connectedAddress,
        balance: parseFloat(ethers.formatEther(nativeBalanceWei)) || 0,
      });
    } catch {
      setNativeBalanceSnapshot(null);
    }
  }, [connectedAddress, resolveActiveChainId]);

  useEffect(() => {
    if (!isOpen) {
      setAvailableCotiBalance(null);
      setNativeBalanceSnapshot(null);
      setCotiBalanceSource("none");
      return;
    }

    console.log("[AmountInputModal][ModalOpen]", {
      connectedAddress,
      podChainId,
      direction,
      tokenSymbol,
      maxAmount,
    });

    const fallback = parseCotiBalance(fallbackCotiBalance);
    if (fallback != null) {
      setAvailableCotiBalance(fallback);
      setCotiBalanceSource("fallback");
    }

    void fetchCotiBalance();
    if (isPodChain) {
      void fetchNativeBalance();
    }
  }, [isOpen, fetchCotiBalance, fetchNativeBalance, isPodChain, fallbackCotiBalance, parseCotiBalance, connectedAddress, direction, tokenSymbol, maxAmount, podChainId]);

  // Safety timeout: clear feeStale after 5s to prevent permanent "Calculating..." state
  useEffect(() => {
    if (!feeStale) return;
    const timeout = setTimeout(() => setFeeStale(false), 5000);
    return () => clearTimeout(timeout);
  }, [feeStale]);

  // ── Price expiry guard: block button and refresh fee when oracle price is about to expire ──
  const [priceExpiring, setPriceExpiring] = useState(false);

  useEffect(() => {
    if (!feeDebugInfo || !amount || parseFloat(amount) <= 0) {
      setPriceExpiring(false);
      return;
    }

    const tokenConfig = SUPPORTED_TOKENS.find(t => t.symbol === (isGoingPrivate ? tokenSymbol.replace("p.", "") : tokenSymbol));
    const timeout = tokenConfig?.timeout ?? 1800;
    const cotiExpiry = Number(feeDebugInfo.cotiLastUpdated) + timeout;
    const tokenExpiry = Number(feeDebugInfo.tokenLastUpdated) + timeout;
    const earliestExpiry = Math.min(cotiExpiry, tokenExpiry);
    const timeLeft = earliestExpiry - Number(feeDebugInfo.blockTimestamp);

    if (timeLeft < 10 && timeLeft > 0) {
      setPriceExpiring(true);
      // Wait for the earliest oracle to expire, then trigger a fee refresh
      const waitMs = (timeLeft + 2) * 1000; // +2s buffer for block propagation
      const timer = setTimeout(() => {
        // Trigger fee recalculation by toggling amount
        onAmountChange(amount);
        setPriceExpiring(false);
      }, waitMs);
      return () => clearTimeout(timer);
    } else {
      setPriceExpiring(false);
    }
  }, [feeDebugInfo, amount, tokenSymbol, isGoingPrivate, onAmountChange]);

  // ── Step-flow state ──────────────────────────────────────────────────────
  const [phase, setPhase] = useState<"input" | "step-flow">("input");
  const [approveStatus, setApproveStatus] = useState<StepStatus>("pending");
  const [transferStatus, setTransferStatus] = useState<StepStatus>("pending");
  const [doneStatus, setDoneStatus] = useState<"pending" | "active">("pending");
  const [stepMessage, setStepMessage] = useState<string | null>(null);
  const [approvalError, setApprovalError] = useState<string | null>(null);
  const [txHash, setTxHash] = useState<string | null>(null);
  const [errorExplorerUrl, setErrorExplorerUrl] = useState<string | null>(null);
  const [trackedPodTxHash, setTrackedPodTxHash] = useState<string | null>(null);
  const [transferError, setTransferError] = useState<string | null>(null);
  const [podTrackerVisible, setPodTrackerVisible] = useState(false);

  const activePodRequest = useMemo(() => {
    if (!showPodRequestTracker || !connectedAddress || podRequests.length === 0) return null;
    // Only bind to the current transfer's tx hash — never match by amount/token,
    // or a prior succeeded PoD request would show as complete on the next portal.
    const podTxHash = trackedPodTxHash ?? (transferStatus === "completed" ? txHash : null);
    if (!podTxHash) return null;

    const wallet = connectedAddress.toLowerCase();
    return podRequests
      .filter(r => r.wallet.toLowerCase() === wallet && r.sourceTxHash === podTxHash)
      .sort((a, b) => b.createdAt - a.createdAt)[0] ?? null;
  }, [showPodRequestTracker, connectedAddress, podRequests, trackedPodTxHash, txHash, transferStatus]);

  // PoD tracker is collapsed by default; user opens it via the (i) button on the Transfer step.
  const showInlinePodTracker = showPodRequestTracker && phase === "step-flow" && podTrackerVisible;

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPhase("input");
      setApproveStatus("pending");
      setTransferStatus("pending");
      setDoneStatus("pending");
      setStepMessage(null);
      maxPinnedRef.current = false;
      setApprovalError(null);
      setTransferError(null);
      setTxHash(null);
      setErrorExplorerUrl(null);
      setTrackedPodTxHash(null);
      setPodTrackerVisible(false);
      setShowTokenDropdown(false);
      setTokenPickConfirmed(false);
    }
  }, [isOpen, isApprovalNeeded]);

  const handleConfirmTokenPick = () => {
    if (!selectedToken) return;
    setShowTokenDropdown(false);
    setTokenPickConfirmed(true);
  };

  const showTokenPickStep = hasActionBarTokenPicker && !tokenPickConfirmed;

  const displayToken = isGoingPrivate ? tokenSymbol.replace("p.", "") : tokenSymbol;
  const publicSymbol = isGoingPrivate
    ? displayToken
    : tokenSymbol.replace(/^p\./, "").replace(/^p(?=[A-Z])/, "");

  // Live on-chain limits — props from useBridgeData can be stale (or missing if the
  // wallet-plugin dist predates limit reads). Re-fetch whenever the modal opens.
  const [liveLimits, setLiveLimits] = useState<{
    minDeposit: string | null;
    maxDeposit: string | null;
    minWithdraw: string | null;
    maxWithdraw: string | null;
  } | null>(null);
  const [limitsLoading, setLimitsLoading] = useState(false);

  useEffect(() => {
    if (!visible || !podChainId || !publicSymbol) {
      setLiveLimits(null);
      setLimitsLoading(false);
      return;
    }
    let cancelled = false;
    setLimitsLoading(true);
    setLiveLimits(null);
    const load = async () => {
      try {
        const config = getChainConfig(podChainId);
        const empty = {
          minDeposit: null as string | null,
          maxDeposit: null as string | null,
          minWithdraw: null as string | null,
          maxWithdraw: null as string | null,
        };
        if (!config) {
          if (!cancelled) setLiveLimits(empty);
          return;
        }
        const token = config.tokens.find(t => !t.isPrivate && t.symbol === publicSymbol);
        const bridgeKey = token?.bridgeAddressKey;
        const bridgeAddress = bridgeKey ? config.addresses[bridgeKey] : undefined;
        if (!token || !bridgeAddress) {
          if (!cancelled) setLiveLimits(empty);
          return;
        }

        const provider = new ethers.JsonRpcProvider(getRpcUrlForChain(podChainId));
        const isPod = config.portalStrategy === "pod-privacy-portal";
        const abi = isPod
          ? POD_PORTAL_ADMIN_ABI
          : token.isNative
            ? BRIDGE_ABI
            : BRIDGE_ERC20_ABI;
        const contract = new ethers.Contract(bridgeAddress, abi, provider);
        const [minDep, maxDep, minWd, maxWd] = await Promise.all([
          contract.minDepositAmount().catch(() => null),
          contract.maxDepositAmount().catch(() => null),
          contract.minWithdrawAmount().catch(() => null),
          contract.maxWithdrawAmount().catch(() => null),
        ]);
        if (cancelled) return;
        // PoD portals use uint128.max (and sometimes larger) as "no max cap".
        const NO_MAX_SENTINEL = (1n << 128n) - 1n;
        const fmt = (v: bigint | null, isMax = false) => {
          if (v == null) return null;
          if (isMax && v >= NO_MAX_SENTINEL) return null;
          return ethers.formatUnits(v, token.decimals);
        };
        setLiveLimits({
          minDeposit: fmt(minDep),
          maxDeposit: fmt(maxDep, true),
          minWithdraw: fmt(minWd),
          maxWithdraw: fmt(maxWd, true),
        });
      } catch (err) {
        console.warn("[AmountInputModal] Failed to load on-chain limits:", err);
        // Settle with nulls so the UI can fall back to props / USD floors.
        if (!cancelled) {
          setLiveLimits({
            minDeposit: null,
            maxDeposit: null,
            minWithdraw: null,
            maxWithdraw: null,
          });
        }
      } finally {
        if (!cancelled) setLimitsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [visible, podChainId, publicSymbol]);

  /**
   * Parse on-chain limit strings; treat 0 / N/A / Error as unset.
   * Dust floors (e.g. 1 wei → 1e-18) are also treated as unset — they are not
   * meaningful UX constraints and Number#toString would render them as "1e-18".
   */
  const parseChainLimit = (value?: string | null): number | null => {
    if (!value || value === "N/A" || value === "Error" || value === "—") return null;
    const n = parseFloat(formatPlainDecimal(value));
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n < DUST_AMOUNT_THRESHOLD) return null;
    return n;
  };

  /** Avoid JS scientific notation (e.g. 1e-18) in limit labels. */
  const formatLimitLabel = (value: number): string => formatPlainDecimal(value);

  /** Amount field values must stay plain decimals — never Number#toString() sci notation. */
  const toAmountInputValue = (value: number, decimals = 4): string => {
    if (!Number.isFinite(value) || value <= 0) return "";
    return truncateDecimalValue(value, decimals);
  };

  const pickStricterMin = (...candidates: Array<number | null | undefined>): string | null => {
    const values = candidates.filter((v): v is number => v != null && Number.isFinite(v));
    if (values.length === 0) return null;
    return formatLimitLabel(Math.max(...values));
  };

  const pickStricterMax = (...candidates: Array<number | null | undefined>): string | null => {
    const values = candidates.filter((v): v is number => v != null && Number.isFinite(v));
    if (values.length === 0) return null;
    return formatLimitLabel(Math.min(...values));
  };

  const dynamicMinimum = amountsForOneDollar?.[displayToken];
  const usdMinimum = isGoingPrivate
    ? (dynamicMinimum
      ? parseFloat(truncateDecimalValue(dynamicMinimum, 6))
      : MINIMUM_PORTAL_IN_AMOUNTS[displayToken]
        ? parseFloat(MINIMUM_PORTAL_IN_AMOUNTS[displayToken])
        : null)
    : null;
  const chainMinimum = isGoingPrivate
    ? parseChainLimit(liveLimits?.minDeposit ?? minDepositAmount)
    : parseChainLimit(liveLimits?.minWithdraw ?? minWithdrawAmount);
  const minimumAmount = pickStricterMin(usdMinimum, chainMinimum);

  const dynamicMaximum = amountsForMaxUsd?.[displayToken];
  const staticMaximum = MINIMUM_PORTAL_IN_AMOUNTS[displayToken]
    ? parseFloat(MINIMUM_PORTAL_IN_AMOUNTS[displayToken]) * PORTAL_IN_MAX_USD
    : null;
  const usdMaximum = isGoingPrivate
    ? (dynamicMaximum
      ? parseFloat(truncateDecimalValue(dynamicMaximum, 6))
      : staticMaximum)
    : null;
  const chainMaximum = isGoingPrivate
    ? parseChainLimit(liveLimits?.maxDeposit ?? maxDepositAmount)
    : parseChainLimit(liveLimits?.maxWithdraw ?? maxWithdrawAmount);
  const maximumAmount = pickStricterMax(usdMaximum, chainMaximum);
  const maximumAmountNumber = maximumAmount !== null ? parseFloat(maximumAmount) : null;

  // ── Transfer logic (called automatically after approve, or directly when no approval needed) ──
  const runTransfer = useCallback(async () => {
    const amountToSend = pendingSendAmountRef.current ?? amount;
    console.log("[AmountInputModal][runTransfer] 🚀 Transfer starting", {
      amount,
      amountToSend,
      direction,
      tokenSymbol,
      connectedAddress,
      isProcessing,
    });

    setTransferError(null);
    setDoneStatus("pending");
    setTxHash(null);
    setErrorExplorerUrl(null);
    setTrackedPodTxHash(null);
    setTransferStatus("loading");
    // Wallet popup first — only switch to "submitted" once we have a hash from transfer-start.
    setStepMessage("Confirm transfer in your wallet");
    try {
      console.log("[AmountInputModal][runTransfer] Calling onConfirm()...");
      await onConfirm((stage, hash) => {
        console.log("[AmountInputModal][runTransfer] onConfirm progress:", stage, hash);
        if ((stage === "transfer-start" || stage === "transfer-complete") && hash) {
          setTxHash(hash);
          if (stage === "transfer-start") {
            setStepMessage("Submitted — waiting for on-chain confirmation");
          }
          if (stage === "transfer-complete") {
            setTrackedPodTxHash(hash);
          }
        }
      }, amountToSend);
      console.log("[AmountInputModal][runTransfer] onConfirm() resolved successfully");
      setTransferStatus("completed");
      if (showPodRequestTracker) {
        setStepMessage("Waiting for PoD to complete…");
      } else {
        setDoneStatus("active");
        setStepMessage("Transfer completed successfully.");
      }
    } catch (err: any) {
      console.error("[AmountInputModal][runTransfer] onConfirm() threw error:", err);
      setTransferStatus("error");
      const hashFromError = extractTxHashFromError(err);
      const podExplorerUrl = extractPodExplorerUrlFromError(err);
      if (hashFromError) {
        setTxHash(hashFromError);
      }
      if (podExplorerUrl) {
        setErrorExplorerUrl(podExplorerUrl);
      }
      setTransferError(formatActionError(err, {
        fallback: "Transfer failed",
        rejected: "User rejected the transfer.",
        feeToken: isPodChain ? portalFeeSymbol : "COTI",
      }));
    } finally {
      pendingSendAmountRef.current = null;
    }
  }, [onConfirm, amount, direction, tokenSymbol, connectedAddress, isProcessing, isPodChain, portalFeeSymbol, showPodRequestTracker]);

  // ── Primary action button (input phase) ─────────────────────────────────
  const handleAction = async () => {
    console.log("[AmountInputModal][handleAction] 🚀 Button clicked", {
      isApprovalNeeded,
      hasOnApprove: !!onApprove,
      amount,
      currentAmount,
      isProcessing,
      isApproving,
      isBridgePaused,
      depositsBlocked,
      connectedAddress,
      phase,
    });

    if (isBridgePaused || depositsBlocked || limitsLoading) {
      return;
    }
    if (
      (minimumAmount !== null && currentAmount < parseFloat(minimumAmount)) ||
      (maximumAmountNumber != null && currentAmount > maximumAmountNumber)
    ) {
      return;
    }

    maxPinnedRef.current = false;
    setApprovalError(null);
    setTransferError(null);
    setApproveStatus("pending");
    setTransferStatus("pending");
    setDoneStatus("pending");
    setStepMessage(null);
    setTxHash(null);
    setErrorExplorerUrl(null);
    setTrackedPodTxHash(null);
    setPodTrackerVisible(false);

    // COTI Portal In: user typed the amount they want to receive. On-chain the portal
    // deducts the portal fee from msg.value, so we must send desired + portalFee.
    // Gas is paid separately by the wallet and must NOT be included in msg.value.
    // Pass via ref — do not call onAmountChange (async state + fee re-quote mid-confirm).
    if (isCotiPortalIn && grossAmount !== null && grossAmountReady) {
      const sendAmount = toAmountInputValue(grossAmount);
      console.log("[AmountInputModal][grossUp] userAmount=%s portalFee=%s sendAmount=%s",
        currentAmount, portalFeeAmount, sendAmount);
      pendingSendAmountRef.current = sendAmount;
    } else {
      pendingSendAmountRef.current = null;
    }

    const needsApproval = isApprovalNeeded && !!onApprove;
    console.log("[AmountInputModal][handleAction] needsApproval:", needsApproval);
    setPhase("step-flow");

    if (needsApproval) {
      // Approval required: show step 1 as loading, fire the approval tx, then transfer immediately.
      setApproveStatus("loading");
      setStepMessage("Confirm approval in your wallet");
      console.log("[AmountInputModal][handleAction] Calling onApprove()...");
      try {
        await onApprove!();
        console.log("[AmountInputModal][handleAction] onApprove() resolved — starting transfer");
        setApproveStatus("completed");
        await runTransfer();
      } catch (err: any) {
        // Only treat as approval error if transfer hasn't started yet
        if (transferStatus !== "loading" && transferStatus !== "completed") {
          console.error("[AmountInputModal][handleAction] onApprove() threw error:", err);
          setApproveStatus("error");
          const hashFromError = extractTxHashFromError(err);
          const podExplorerUrl = extractPodExplorerUrlFromError(err);
          if (hashFromError) {
            setTxHash(hashFromError);
          }
          if (podExplorerUrl) {
            setErrorExplorerUrl(podExplorerUrl);
          }
          setApprovalError(formatActionError(err, {
            fallback: "Approval failed",
            rejected: "User rejected approval.",
            feeToken: isPodChain ? portalFeeSymbol : "COTI",
          }));
        }
      }
    } else {
      // No approval needed — skip straight to transfer.
      console.log("[AmountInputModal][handleAction] No approval needed — starting transfer immediately");
      setApproveStatus("completed");
      await runTransfer();
    }
  };

  // ── Reset to input phase on error ────────────────────────────────────────
  const handleRetry = () => {
    maxPinnedRef.current = false;
    setPhase("input");
    setApproveStatus("pending");
    setTransferStatus("pending");
    setDoneStatus("pending");
    setStepMessage(null);
    setApprovalError(null);
    setTransferError(null);
    setTxHash(null);
    setErrorExplorerUrl(null);
    setTrackedPodTxHash(null);
    setPodTrackerVisible(false);
  };

  const getButtonText = () => {
    if (isApproving) return "Approving...";
    if (isProcessing) return "Processing...";
    if (isApprovalNeeded) return `Portal ${isGoingPrivate ? "In" : "Out"} ${displayToken}`;
    return `Portal ${isGoingPrivate ? "In" : "Out"}`;
  };

  const currentAmount = parseFloat(amount) || 0;

  const portalFeeAmount = useMemo(() => {
    if (isPodChain) {
      if (!portalFee || portalFee === "0") return 0;
      return parseFloat(portalFee) || 0;
    }
    if (!portalFeeCoti || portalFeeCoti === "0") return 0;
    return Math.ceil(parseFloat(portalFeeCoti) * 100) / 100;
  }, [isPodChain, portalFee, portalFeeCoti]);

  const podInboxFeeAmount = useMemo(() => {
    if (!isPodChain || !podInboxFee || podInboxFee === "0") return 0;
    return parseFloat(podInboxFee) || 0;
  }, [isPodChain, podInboxFee]);

  const l1GasFeeAmount = useMemo(() => {
    if (!isPodChain || isGasEstimating) return 0;
    if (l1GasFee) return parseFloat(l1GasFee) || 0;
    return parseFloat(estimatedGasFee || "0") || 0;
  }, [isPodChain, l1GasFee, estimatedGasFee, isGasEstimating]);

  const gasFeeAmount = useMemo(() => {
    if (isPodChain) return l1GasFeeAmount;
    if (estimatedGasSymbol !== "COTI") return 0;
    if (isGasEstimating) return 0;
    return parseFloat(estimatedGasFee || "0") || 0;
  }, [isPodChain, l1GasFeeAmount, estimatedGasFee, estimatedGasSymbol, isGasEstimating]);

  const totalPodNativeFees = portalFeeAmount + podInboxFeeAmount + l1GasFeeAmount;
  const nativeDepositAmount = isPodChain && isGoingPrivate && displayToken === portalFeeSymbol ? currentAmount : 0;

  const totalCotiFee = portalFeeAmount + gasFeeAmount;

  // PoD (Sepolia/Fuji): log portal + inbox fee parts while UI shows their sum as Portal Fee
  useEffect(() => {
    if (!isOpen || !isPodChain || currentAmount <= 0) return;
    if (feeStale || priceExpiring || isGasEstimating) return;
    console.log('[AmountInputModal][PoD Portal Fee]', {
      portalFee: portalFee ?? '0',
      podInboxFee: podInboxFee ?? '0',
      combined: (portalFeeAmount + podInboxFeeAmount).toString(),
      symbol: portalFeeSymbol,
    });
  }, [
    isOpen,
    isPodChain,
    currentAmount,
    feeStale,
    priceExpiring,
    isGasEstimating,
    portalFee,
    podInboxFee,
    portalFeeAmount,
    podInboxFeeAmount,
    portalFeeSymbol,
  ]);

  // ── Effective max for COTI Portal In ─────────────────────────────────────
  // COTI is a special case: the fee is paid in the same token being portaled,
  // so the actual sendable max = balance - fees. This value drives the slider,
  // the "Max available" label, and the Max button.
  // Until the portal fee is known, effectiveMaxAmount is null ("still loading").
  const isCotiPortalIn = isGoingPrivate && displayToken === "COTI";

  // ── Gross-up for COTI Portal In ──────────────────────────────────────────
  // The user types the amount they *want to receive*. msg.value must be
  // desiredAmount + portalFee (gas is wallet-paid separately, not in deposit).
  const grossAmount = useMemo(() => {
    if (!isCotiPortalIn || currentAmount <= 0) return null;
    return currentAmount + portalFeeAmount;
  }, [isCotiPortalIn, currentAmount, portalFeeAmount]);

  // Ready when all fee inputs are settled (no stale portal fee, no gas estimating, no price refresh)
  const grossAmountReady =
    isCotiPortalIn &&
    currentAmount > 0 &&
    !feeStale &&
    !isGasEstimating &&
    !priceExpiring;

  // For COTI Portal In, trigger fee estimation at max amount on modal open
  // so we can know the portal fee before the user interacts.
  const [maxFeeEstimated, setMaxFeeEstimated] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setMaxFeeEstimated(false);
      return;
    }
    // When modal opens for COTI Portal In and no amount is set yet,
    // set amount to maxAmount to trigger the parent's fee calculation.
    if (isCotiPortalIn && !maxFeeEstimated && maxAmount > 0 && (!amount || parseFloat(amount) === 0)) {
      console.log("[AmountInputModal][effectiveMax] Auto-setting amount to maxAmount to trigger fee calc", { maxAmount });
      onAmountChange(toAmountInputValue(maxAmount));
      setMaxFeeEstimated(true);
    }
  }, [isOpen, isCotiPortalIn, maxFeeEstimated, maxAmount, amount, onAmountChange]);

  // Once the probe fee quote settles, store fee-at-max and reset amount.
  // Portal fee may be null/0 (oracle or free deposit) while gas fee still quotes —
  // still finish the probe so Max/slider are not stuck loading forever.
  const [maxFeeApplied, setMaxFeeApplied] = useState(false);
  const [portalFeeAtMax, setPortalFeeAtMax] = useState<number | null>(null);
  const [probeQuoteStarted, setProbeQuoteStarted] = useState(false);
  useEffect(() => {
    if (!isOpen) {
      setProbeQuoteStarted(false);
      return;
    }
    if (maxFeeEstimated && isGasEstimating) {
      setProbeQuoteStarted(true);
    }
  }, [isOpen, maxFeeEstimated, isGasEstimating]);

  useEffect(() => {
    if (!isOpen) {
      setMaxFeeApplied(false);
      setPortalFeeAtMax(null);
      return;
    }
    if (!isCotiPortalIn || !maxFeeEstimated || maxFeeApplied) return;
    // Don't finish before the debounced quote has actually started+finished
    if (!probeQuoteStarted || isGasEstimating) return;
    if (!amount || parseFloat(amount) <= 0) return;

    const portalFee =
      portalFeeCoti && parseFloat(portalFeeCoti) > 0
        ? Math.ceil(parseFloat(portalFeeCoti) * 100) / 100
        : 0;
    const gasFee =
      estimatedGasSymbol === "COTI" && estimatedGasFee
        ? parseFloat(estimatedGasFee) || 0
        : 0;
    const totalFee = portalFee + gasFee;

    console.log("[AmountInputModal][effectiveMax] Portal fee at max settled", {
      portalFee,
      gasFee,
      totalFee,
      portalFeeCoti,
      estimatedGasFee,
    });
    setPortalFeeAtMax(totalFee);
    // Reset amount to empty so user starts fresh with correct max shown
    onAmountChange("");
    setMaxFeeApplied(true);
  }, [
    isOpen,
    isCotiPortalIn,
    maxFeeEstimated,
    maxFeeApplied,
    probeQuoteStarted,
    isGasEstimating,
    amount,
    portalFeeCoti,
    estimatedGasFee,
    estimatedGasSymbol,
    onAmountChange,
  ]);

  const balanceMaxAmount = useMemo(() => {
    if (!isCotiPortalIn) return maxAmount;

    // Probe finished (including fee-at-max of 0)
    if (portalFeeAtMax !== null) {
      const result = Math.max(0, maxAmount - portalFeeAtMax * 1.1);
      console.log("[AmountInputModal][effectiveMax] COTI Portal In — using portalFeeAtMax", {
        maxAmount, portalFeeAtMax, effectiveMax: result,
      });
      return result;
    }

    // Fallback: if current totalCotiFee is known (user already entered an amount).
    // Skip during the max-balance fee probe — probe amount must not end loading early.
    if (totalCotiFee > 0 && (!maxFeeEstimated || maxFeeApplied)) {
      const result = Math.max(0, maxAmount - totalCotiFee * 1.1);
      console.log("[AmountInputModal][effectiveMax] COTI Portal In — using current totalCotiFee", {
        maxAmount, totalCotiFee, effectiveMax: result,
      });
      return result;
    }

    // No fee info available yet — return null to signal "still calculating"
    console.log("[AmountInputModal][effectiveMax] COTI Portal In — waiting for portal fee");
    return null;
  }, [isCotiPortalIn, maxAmount, portalFeeAtMax, totalCotiFee, maxFeeEstimated, maxFeeApplied]);

  // Balance-based max, further capped by the Portal In USD notional limit when set.
  const effectiveMaxAmount = useMemo(() => {
    if (balanceMaxAmount === null) return null;
    if (maximumAmountNumber == null || Number.isNaN(maximumAmountNumber)) return balanceMaxAmount;
    return Math.min(balanceMaxAmount, maximumAmountNumber);
  }, [balanceMaxAmount, maximumAmountNumber]);

  // Whether the effective max is still being determined (COTI Portal In waiting for fees)
  const isEffectiveMaxLoading =
    isCotiPortalIn && !maxFeeApplied && (effectiveMaxAmount === null || maxFeeEstimated);

  // Log on every render to confirm latest code is running
  useEffect(() => {
    if (isOpen) {
      if (import.meta.env.DEV) console.log("[AmountInputModal][v3] Modal open — effectiveMax logic active", {
        isCotiPortalIn,
        effectiveMaxAmount,
        isEffectiveMaxLoading,
        maxAmount,
        totalCotiFee,
        portalFeeCoti,
        maxFeeEstimated,
        maxFeeApplied,
      });
    }
  }, [isOpen, isCotiPortalIn, effectiveMaxAmount, isEffectiveMaxLoading, maxAmount, totalCotiFee, portalFeeCoti, maxFeeEstimated, maxFeeApplied]);

  const percentage = (effectiveMaxAmount ?? 0) > 0 ? (currentAmount / (effectiveMaxAmount ?? 1)) * 100 : 0;

  const portalFeeReady = currentAmount > 0 && !feeStale && !priceExpiring;
  const gasFeeReady = isPodChain ? !isGasEstimating : (estimatedGasSymbol !== "COTI" || !isGasEstimating);

  const cotiAvailableForFees = useMemo(() => {
    if (!isPodChain) {
      if (availableCotiBalance === null) return null;
      if (isGoingPrivate && displayToken === "COTI") {
        return Math.max(0, availableCotiBalance - currentAmount);
      }
      return availableCotiBalance;
    }
    return null;
  }, [isPodChain, availableCotiBalance, isGoingPrivate, displayToken, currentAmount]);

  const nativeAvailableForFees = useMemo(() => {
    if (!isPodChain || availableNativeBalance === null) return null;
    return Math.max(0, availableNativeBalance - nativeDepositAmount);
  }, [isPodChain, availableNativeBalance, nativeDepositAmount]);

  const notEnoughCotiForPortalFee =
    !isPodChain &&
    portalFeeReady &&
    portalFeeAmount > 0 &&
    cotiAvailableForFees !== null &&
    portalFeeAmount > cotiAvailableForFees;

  const notEnoughCotiForTotalFees =
    !isPodChain &&
    portalFeeReady &&
    gasFeeReady &&
    totalCotiFee > 0 &&
    cotiAvailableForFees !== null &&
    totalCotiFee > cotiAvailableForFees;

  const notEnoughCotiForFees = notEnoughCotiForPortalFee || notEnoughCotiForTotalFees;

  // Hard stop independent of the fee quote: with nothing left for gas, no PoD
  // portal tx can succeed. The quote itself can fail for the same reason the
  // tx would, which used to leave the button enabled with no warning.
  const noNativeLeftForFees =
    isPodChain &&
    nativeAvailableForFees !== null &&
    nativeAvailableForFees <= 0;

  const notEnoughNativeForFees =
    noNativeLeftForFees ||
    (isPodChain &&
      portalFeeReady &&
      gasFeeReady &&
      totalPodNativeFees > 0 &&
      nativeAvailableForFees !== null &&
      totalPodNativeFees > nativeAvailableForFees);

  const notEnoughForFees = notEnoughCotiForFees || notEnoughNativeForFees;

  useEffect(() => {
    if (!isOpen || phase !== "input") return;

    console.log("[AmountInputModal][CotiFeeCheck]", {
      direction,
      displayToken,
      currentAmount,
      maxAmount,
      portalFeeCoti,
      portalFeeAmount,
      estimatedGasFee,
      gasFeeAmount,
      estimatedGasSymbol,
      totalCotiFee,
      availableCotiBalance,
      cotiBalanceSource,
      fallbackCotiBalance,
      cotiAvailableForFees,
      feeStale,
      isGasEstimating,
      priceExpiring,
      portalFeeReady,
      gasFeeReady,
      notEnoughCotiForPortalFee,
      notEnoughCotiForTotalFees,
      notEnoughCotiForFees,
      isPodChain,
      availableNativeBalance,
      nativeAvailableForFees,
      totalPodNativeFees,
      noNativeLeftForFees,
      notEnoughNativeForFees,
    });
  }, [
    isOpen,
    phase,
    direction,
    displayToken,
    currentAmount,
    maxAmount,
    portalFeeCoti,
    portalFeeAmount,
    estimatedGasFee,
    gasFeeAmount,
    estimatedGasSymbol,
    totalCotiFee,
    availableCotiBalance,
    cotiBalanceSource,
    fallbackCotiBalance,
    cotiAvailableForFees,
    feeStale,
    isGasEstimating,
    priceExpiring,
    portalFeeReady,
    gasFeeReady,
    notEnoughCotiForPortalFee,
    notEnoughCotiForTotalFees,
    notEnoughCotiForFees,
    isPodChain,
    availableNativeBalance,
    nativeAvailableForFees,
    totalPodNativeFees,
    noNativeLeftForFees,
    notEnoughNativeForFees,
  ]);

  const handleSliderChange = (values: number[]) => {
    if (effectiveMaxAmount === null) return;
    const percent = values[0];
    const newAmount = (effectiveMaxAmount * percent) / 100;
    maxPinnedRef.current = percent >= 100;
    onAmountChange(toAmountInputValue(newAmount));
  };

  const handleMaxClick = () => {
    if (effectiveMaxAmount === null) return;
    maxPinnedRef.current = true;
    onAmountChange(toAmountInputValue(effectiveMaxAmount));
  };

  // After Max, fees re-quote for the new amount. If effective max shrinks below
  // the pinned amount, clamp down once quotes settle — avoids a false
  // "Insufficient balance" from a mid-flight fee/maxAmount change.
  useEffect(() => {
    if (!maxPinnedRef.current) return;
    if (!isCotiPortalIn || !isOpen || phase !== "input") return;
    if (effectiveMaxAmount === null || feeStale || isGasEstimating || isEffectiveMaxLoading) return;

    const next = parseFloat(truncateDecimalValue(effectiveMaxAmount, 4));
    if (!Number.isFinite(next)) return;
    // Only clamp downward — never chase fee updates upward (prevents loops).
    if (currentAmount > next + 1e-9) {
      onAmountChange(toAmountInputValue(next));
    }
  }, [
    isCotiPortalIn,
    isOpen,
    phase,
    effectiveMaxAmount,
    feeStale,
    isGasEstimating,
    isEffectiveMaxLoading,
    currentAmount,
    onAmountChange,
  ]);

  const getIconName = (symbol: string) => {
    const token = SUPPORTED_TOKENS.find(t => t.symbol === symbol);
    if (token) return resolveTokenIcon(symbol, token.icon);

    const baseSymbol = symbol.replace("p.", "");
    const baseToken = SUPPORTED_TOKENS.find(t => t.symbol === baseSymbol);
    if (baseToken) return resolveTokenIcon(baseSymbol, baseToken.icon);

    const s = symbol.toLowerCase();
    if (s.includes("night")) return "/icons/night.svg";
    if (s.includes("coti")) return "coti";
    if (s.includes("eth")) return "weth";
    if (s.includes("btc")) return "wbtc";
    if (s.includes("usdc")) return "usdc";
    if (s.includes("ada")) return "wada";
    if (s.includes("usdt")) return "usdt";
    return "coti";
  };

  const iconName = getIconName(tokenSymbol);

  // Determine which step has an error so we can show the retry button
  const isPodRequestForCurrentTransfer =
    transferStatus === "completed" &&
    (trackedPodTxHash != null || txHash != null);

  const isPodFlowFailed =
    showPodRequestTracker &&
    isPodRequestForCurrentTransfer &&
    activePodRequest != null &&
    (activePodRequest.status === "failed" || activePodRequest.status === "callback-errored");

  const isPodFlowSucceeded =
    showPodRequestTracker &&
    isPodRequestForCurrentTransfer &&
    activePodRequest?.status === "succeeded";

  const isFlowComplete = showPodRequestTracker ? isPodFlowSucceeded : doneStatus === "active";

  const isWaitingForPod =
    showPodRequestTracker &&
    phase === "step-flow" &&
    isPodRequestForCurrentTransfer &&
    !isPodFlowSucceeded &&
    !isPodFlowFailed;

  const hasError = approveStatus === "error" || transferStatus === "error" || isPodFlowFailed;

  const isLockedForProcessing =
    phase === "step-flow" &&
    !showTokenPickStep &&
    !hasError &&
    !isFlowComplete;

  const handleClose = () => {
    if (isLockedForProcessing) return;
    onClose();
  };

  const podFailureMessage =
    activePodRequest?.status === "callback-errored"
      ? "PoD callback failed."
      : "PoD request failed.";

  const statusMessage = hasError
    ? (approvalError || transferError || (isPodFlowFailed ? podFailureMessage : null))
    : isFlowComplete
      ? null
      : isWaitingForPod
        ? "Waiting for PoD to complete…"
        : stepMessage;

  const isAwaitingWallet =
    (approveStatus === "loading" || transferStatus === "loading") && !txHash;
  const isAwaitingOnChain =
    transferStatus === "loading" && !!txHash;

  const processingButtonLabel = approveStatus === "loading"
    ? "Confirm in wallet…"
    : isAwaitingWallet
      ? "Confirm in wallet…"
      : isAwaitingOnChain
        ? "Confirming on-chain…"
        : isWaitingForPod
          ? "Waiting for PoD…"
          : "Processing…";

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={`fixed inset-0 z-50 flex justify-center sm:p-4 ${showTokenPickStep ? "items-center" : "items-end sm:items-center"}`}
        >
          <div className="absolute inset-0 modal-overlay-blur" onClick={handleClose} />

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 24 }}
            transition={{ duration: 0.2 }}
            className={`relative w-full max-w-md border border-border modal-compact p-4 sm:p-5 shadow-2xl rounded-2xl ${showTokenDropdown ? "overflow-visible" : "max-h-[92dvh] overflow-y-auto overscroll-contain sm:max-h-none sm:overflow-visible"}`}
          >

              {/* ── Header ─────────────────────────────────────────────── */}
              <div className="mb-2 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-foreground sm:text-xl">
                    Portal {isGoingPrivate ? "In" : "Out"}
                  </h2>
                  {!showTokenPickStep && (
                    <div className="flex items-center gap-2 mt-1">
                      <TokenIcon icon={iconName} size="sm" isPrivate={!isGoingPrivate} />
                      <ArrowRight className="w-3 h-3 text-muted-foreground" />
                      <TokenIcon icon={iconName} size="sm" isPrivate={isGoingPrivate} />
                    </div>
                  )}
                </div>
                {isLockedForProcessing ? (
                  <div className="h-9 w-9 shrink-0 sm:h-8 sm:w-8" aria-hidden />
                ) : (
                  <button
                    onClick={handleClose}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border text-muted-foreground transition-colors hover:border-border hover:text-foreground sm:h-8 sm:w-8"
                    aria-label="Close"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              {/* Blur overlay when dropdown is open */}
              {showTokenDropdown && (
                <div className="absolute inset-0 backdrop-blur-sm modal-overlay-subtle z-[5] rounded-2xl pointer-events-none" />
              )}

              {/* ── ActionBar token pick step ───────────────────────────── */}
              {showTokenPickStep && (
                <div className="relative z-[20]">
                  <div className="relative mb-6">
                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setShowTokenDropdown(!showTokenDropdown)}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-border bg-muted/50 hover:border-border transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          {selectedToken && (
                            <>
                              <TokenIcon icon={selectedToken.icon} size="sm" isPrivate={!isGoingPrivate} />
                              <div className="flex flex-col text-left">
                                <span className="text-foreground font-semibold text-sm">
                                  {selectedToken.publicName}
                                </span>
                                <span className="text-muted-foreground text-xs">
                                  {isGoingPrivate ? selectedToken.publicSymbol : selectedToken.privateSymbol}
                                </span>
                              </div>
                            </>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          {selectedToken && (
                            <span className="text-xs text-muted-foreground">
                              {formatBalanceMinDecimals(getPickerBalance(selectedToken))}
                            </span>
                          )}
                          <ChevronDown className="w-4 h-4 text-muted-foreground" />
                        </div>
                      </button>

                      <AnimatePresence>
                        {showTokenDropdown && tokenPairs && (
                          <motion.div
                            initial={{ opacity: 0, y: -5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            className="absolute top-full left-0 right-0 mt-1 max-h-60 overflow-y-auto overscroll-contain rounded-xl border border-border modal-dropdown z-[15] shadow-2xl"
                          >
                            {tokenPairs.map((token) => (
                              <button
                                key={token.publicSymbol}
                                type="button"
                                onClick={() => handleSelectToken(token)}
                                className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-muted/50 transition-colors"
                              >
                                <div className="flex items-center gap-3">
                                  <TokenIcon icon={token.icon} size="sm" isPrivate={!isGoingPrivate} />
                                  <div className="flex flex-col text-left">
                                    <span className="text-foreground text-sm font-medium">
                                      {token.publicName}
                                    </span>
                                    <span className="text-muted-foreground text-xs">
                                      {isGoingPrivate ? token.publicSymbol : token.privateSymbol}
                                    </span>
                                  </div>
                                </div>
                                <span className="text-xs text-muted-foreground">
                                  {formatBalanceMinDecimals(getPickerBalance(token))}
                                </span>
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <Button
                    type="button"
                    onClick={handleConfirmTokenPick}
                    disabled={!selectedToken}
                    className="w-full font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ height: "40px", borderRadius: "8px", fontSize: "14px" }}
                  >
                    OK
                  </Button>
                </div>
              )}

              {/* ── Input Phase ─────────────────────────────────────────── */}
              {!showTokenPickStep && phase === "input" && (
                <>
                  {/* Amount input */}
                  <div className="text-center mb-2 relative z-10">
                    <div className="flex items-baseline justify-center gap-1">
                      <div className="relative inline-block min-w-[60px]">
                        <Input
                          type="text"
                          value={isEffectiveMaxLoading ? "" : amount}
                          onChange={(e) => {
                            if (isEffectiveMaxLoading) return;
                            const val = e.target.value;
                            if (val === "" || /^\d*\.?\d{0,8}$/.test(val)) {
                              const newAmount = parseFloat(val) || 0;
                              // Prevent entering more digits if insufficient balance is shown
                              if (effectiveMaxAmount !== null && currentAmount > effectiveMaxAmount && newAmount > currentAmount) {
                                return;
                              }
                              maxPinnedRef.current = false;
                              onAmountChange(val);
                            }
                          }}
                          placeholder="0.00"
                          disabled={isEffectiveMaxLoading}
                          className="
                            bg-transparent border-none text-center text-4xl md:text-5xl font-semibold
                            text-foreground placeholder:text-muted-foreground focus-visible:ring-0 p-0 h-auto w-full
                          "
                          style={{ caretColor: "white" }}
                        />
                      </div>
                      <span className="text-lg text-foreground font-normal">{displayToken}</span>
                    </div>
                    <p className="text-muted-foreground text-[13px] mt-1">
                      Max available:{" "}
                      {isEffectiveMaxLoading ? (
                        <span className="inline-flex items-center gap-1">
                          <Loader2 className="w-3 h-3 animate-spin inline" />
                          Calculating...
                        </span>
                      ) : (
                        <>
                          {(effectiveMaxAmount ?? 0).toLocaleString(undefined, {
                            minimumFractionDigits: 6,
                            maximumFractionDigits: 6,
                          })}{" "}
                          {displayToken}
                        </>
                      )}
                    </p>
                    {minimumAmount && (
                      <p className="text-muted-foreground text-[13px]">
                        Minimum: {minimumAmount} {displayToken}
                      </p>
                    )}
                    {balanceMaxAmount !== null &&
                      currentAmount > balanceMaxAmount &&
                      // Hide transient overage while COTI Portal In fees re-quote after Max
                      !(isCotiPortalIn && (feeStale || isGasEstimating)) && (
                      <p className="text-red-400 text-[12px] mt-2 font-medium">
                        Insufficient balance
                      </p>
                    )}
                    {maximumAmountNumber != null &&
                      currentAmount > maximumAmountNumber &&
                      (balanceMaxAmount === null || currentAmount <= balanceMaxAmount) && (
                        <p className="text-red-400 text-[12px] mt-2 font-medium">
                          Amount above maximum ({maximumAmount} {displayToken})
                        </p>
                      )}
                    {minimumAmount &&
                      currentAmount > 0 &&
                      currentAmount < parseFloat(minimumAmount) && (
                        <p className="text-red-400 text-[12px] mt-2 font-medium">
                          Amount below minimum ({minimumAmount} {displayToken})
                        </p>
                      )}
                  </div>

                  <AmountPercentSlider
                    value={percentage > 100 ? 100 : percentage}
                    onValueChange={(percent) => handleSliderChange([percent])}
                    onMax={handleMaxClick}
                    disabled={
                      isEffectiveMaxLoading ||
                      effectiveMaxAmount === null ||
                      effectiveMaxAmount <= 0
                    }
                  />

                  {/* Fee Breakdown */}
                  {(() => {
                    const isFeeCalculating = feeStale && currentAmount > 0;
                    const isPriceRefreshing = priceExpiring && currentAmount > 0;

                    let hasPortalFee = false;
                    let displayFee = '0';
                    const feeSymbol = isPodChain ? portalFeeSymbol : 'COTI';

                    if (isPodChain) {
                      const portalPart = portalFee && portalFee !== '0' ? (parseFloat(portalFee) || 0) : 0;
                      const inboxPart = podInboxFee && podInboxFee !== '0' ? (parseFloat(podInboxFee) || 0) : 0;
                      const combined = portalPart + inboxPart;
                      hasPortalFee = combined > 0;
                      // Cap PoD portal fee display at 6 decimals (trim trailing zeros)
                      displayFee = hasPortalFee
                        ? combined.toFixed(6).replace(/\.?0+$/, '')
                        : '0';
                    } else {
                      hasPortalFee = !!portalFeeCoti && portalFeeCoti !== '0';
                      displayFee = hasPortalFee
                        ? (Math.ceil(parseFloat(portalFeeCoti!) * 100) / 100).toFixed(2)
                        : '0';
                    }

                    const isPodFeeLoading =
                      isPodChain && currentAmount > 0 && (isFeeCalculating || isPriceRefreshing || isGasEstimating);
                    const isCotiFeeLoading = !isPodChain && (isFeeCalculating || isPriceRefreshing);
                    const showFeeLoading = isPodFeeLoading || isCotiFeeLoading;

                    return (
                      <div
                        className={`mt-10 space-y-2 text-[13px] text-foreground relative z-10 ${
                          isBridgePaused || depositsBlocked || notEnoughForFees ? 'mb-2' : 'mb-8'
                        }`}
                      >
                        <div className="flex justify-between items-center">
                          <span>Portal Fee</span>
                          <div className="flex items-center gap-2">
                            {showFeeLoading && (
                              <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                            )}
                            <span>
                              {showFeeLoading
                                ? 'Loading...'
                                : hasPortalFee
                                  ? `${displayFee} ${feeSymbol}`
                                  : '0'}
                            </span>
                          </div>
                        </div>
                        {/* Amount to be sent — only after the max-fee probe finishes and the user entered an amount */}
                        {isCotiPortalIn && currentAmount > 0 && !isEffectiveMaxLoading && (
                          <div className="flex justify-between items-center border-t border-border pt-2 mt-1 font-medium">
                            <span>Amount to be sent</span>
                            <div className="flex items-center gap-2">
                              {!grossAmountReady && (
                                <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                              )}
                              <span>
                                {grossAmountReady && grossAmount !== null
                                  ? `${grossAmount.toFixed(4)} COTI`
                                  : "Calculating…"}
                              </span>
                            </div>
                          </div>
                        )}
                        {/* debug: fee oracle timing info — uncomment to reactivate
                        {feeDebugInfo && (() => {
                          const tokenConfig = SUPPORTED_TOKENS.find(t => t.symbol === displayToken);
                          const timeout = tokenConfig?.timeout ?? 1800;
                          const cotiExpiry = Number(feeDebugInfo.cotiLastUpdated) + timeout;
                          const tokenExpiry = Number(feeDebugInfo.tokenLastUpdated) + timeout;
                          const earliestExpiry = Math.min(cotiExpiry, tokenExpiry);
                          const timeLeft = earliestExpiry - Number(feeDebugInfo.blockTimestamp);
                          const fmt = (ts: number) => new Date(ts * 1000).toISOString().replace('T', ' ').slice(0, 19);
                          return (
                            <>
                              <div className="mt-2 pt-2 border-t border-border space-y-1 text-[11px] text-red-400">
                                <div>debug: cotiLastUpdated = {fmt(Number(feeDebugInfo.cotiLastUpdated))}</div>
                                <div>debug: tokenLastUpdated = {fmt(Number(feeDebugInfo.tokenLastUpdated))}</div>
                                <div>debug: earliestExpiry = {fmt(earliestExpiry)}</div>
                                <div>debug: blockTimestamp = {fmt(Number(feeDebugInfo.blockTimestamp))}</div>
                              </div>
                              <div className="text-[11px] text-foreground mt-1">
                                time left: {timeLeft} seconds
                              </div>
                            </>
                          );
                        })()}
                        */}
                      </div>
                    );
                  })()}

                  {/* Bridge Paused Banner */}
                  {isBridgePaused && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-[13px] mb-2">
                      <Info className="w-4 h-4 shrink-0" />
                      <span>Bridge is Paused. Transactions are currently disabled.</span>
                    </div>
                  )}

                  {/* Deposits Disabled Banner (Portal In only) */}
                  {!isBridgePaused && depositsBlocked && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-orange-400 text-[13px] mb-2">
                      <Info className="w-4 h-4 shrink-0" />
                      <span>Deposits are currently disabled for this bridge. Portal Out is still available.</span>
                    </div>
                  )}

                  {notEnoughForFees && (
                    <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-[13px] mb-2">
                      <div className="flex flex-col gap-0.5">
                        <span>
                          ⓘ Insufficient {isPodChain ? portalFeeSymbol : "COTI"} balance for the network fee.
                        </span>
                        <span>
                          Add {isPodChain ? portalFeeSymbol : "COTI"} to your wallet and try again.
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Action Button */}
                  {(() => {
                    const btnDisabledReasons = {
                      isBridgePaused,
                      depositsBlocked,
                      limitsLoading,
                      isEffectiveMaxLoading,
                      notEnoughForFees,
                      feeStale,
                      priceExpiring,
                      cotiPortalInGasCalculating: isCotiPortalIn && isGasEstimating,
                      amountZero: currentAmount <= 0,
                      isProcessing,
                      isApproving,
                      exceedsMax: effectiveMaxAmount !== null && currentAmount > effectiveMaxAmount,
                      aboveUsdMax: maximumAmountNumber != null && currentAmount > maximumAmountNumber,
                      belowMin: minimumAmount !== null && currentAmount < parseFloat(minimumAmount),
                    };
                    const isDisabled = Object.values(btnDisabledReasons).some(Boolean);
                    if (isDisabled) {
                      console.log("[AmountInputModal][ActionButton] DISABLED — reasons:", btnDisabledReasons);
                    }
                    return null;
                  })()}
                  <Button
                    onClick={handleAction}
                    disabled={
                      isBridgePaused ||
                      depositsBlocked ||
                      limitsLoading ||
                      isEffectiveMaxLoading ||
                      notEnoughForFees ||
                      feeStale ||
                      priceExpiring ||
                      (isCotiPortalIn && isGasEstimating) ||
                      currentAmount <= 0 ||
                      isProcessing ||
                      isApproving ||
                      (effectiveMaxAmount !== null && currentAmount > effectiveMaxAmount) ||
                      (maximumAmountNumber != null && currentAmount > maximumAmountNumber) ||
                      (minimumAmount !== null && currentAmount < parseFloat(minimumAmount))
                    }
                    className="w-full font-medium relative z-10 bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ height: "40px", borderRadius: "8px", fontSize: "14px" }}
                  >
                    {isProcessing || isApproving ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {getButtonText()}
                      </span>
                    ) : (
                      getButtonText()
                    )}
                  </Button>
                </>
              )}

              {/* ── Step-Flow Phase (Portal In & Portal Out) ──────────────── */}
              {!showTokenPickStep && phase === "step-flow" && (
                <div className="flex flex-col gap-5 relative z-10">
                  {/* Amount summary */}
                  <div className="text-center modal-inset border border-border rounded-2xl p-4">
                    <span className="text-4xl font-semibold text-foreground">{amount}</span>
                    <span className="text-lg text-muted-foreground ml-2">{displayToken}</span>
                  </div>

                  {/* Steps card */}
                  <div className="modal-inset border border-border rounded-2xl p-6">
                    <div className="flex items-center justify-between">
                      <>
                        <StepCircle
                          stepNum={1}
                          status={isFlowComplete ? "completed" : approveStatus}
                          label="Approve"
                        />
                        <StepConnector active={approveStatus === "completed" || isFlowComplete} />
                      </>
                      <StepCircle
                        stepNum={2}
                        status={isFlowComplete ? "completed" : transferStatus}
                        label="Transfer Tokens"
                        labelAction={
                          showPodRequestTracker ? (
                            <button
                              type="button"
                              onClick={() => setPodTrackerVisible(v => !v)}
                              aria-label={podTrackerVisible ? "Hide PoD progress" : "Show PoD progress"}
                              aria-pressed={podTrackerVisible}
                              className={`mt-px inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full transition-colors sm:mt-0 ${
                                podTrackerVisible
                                  ? "text-primary hover:text-primary/80"
                                  : "text-muted-foreground hover:text-foreground"
                              }`}
                            >
                              <Info className="w-3.5 h-3.5" strokeWidth={2.25} />
                            </button>
                          ) : undefined
                        }
                      />
                      <StepConnector active={transferStatus === "completed" || isFlowComplete} />
                      <StepCircle
                        stepNum={3}
                        status={isFlowComplete ? "completed" : isPodFlowFailed ? "error" : "pending"}
                        label="Completed"
                      />
                    </div>
                  </div>

                  {showInlinePodTracker && (
                    <PodRequestTracker
                      variant="compact"
                      skeleton={!activePodRequest}
                      chainId={podChainId}
                      requests={activePodRequest ? [activePodRequest] : []}
                    />
                  )}

                  {/* Phase info message */}
                  {statusMessage && (
                    <div className={`text-[13px] rounded-lg px-3 py-2 border ${hasError ? "text-red-400 bg-red-400/10 border-red-400/20" : "text-muted-foreground bg-muted/50 border-border"}`}>
                      <div className="flex items-start gap-2">
                        <Info className="w-4 h-4 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          {/* Strip raw URLs from the message text — the Failed TX / PoD link below is the clickable version */}
                          <span className="break-words text-pretty">{statusMessage.replace(/\s*https?:\/\/\S+/g, "")}</span>
                          {hasError && errorExplorerUrl && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-red-400/20">
                              <span className="text-red-300/80 shrink-0">PoD request</span>
                              <a
                                href={errorExplorerUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary/80 font-mono truncate transition-colors"
                                title={errorExplorerUrl}
                              >
                                {errorExplorerUrl.replace(/^https?:\/\//, "").replace(/\/$/, "")}
                              </a>
                            </div>
                          )}
                          {hasError && !errorExplorerUrl && txHash && (
                            <div className="flex items-center gap-2 mt-2 pt-2 border-t border-red-400/20">
                              <span className="text-red-300/80 shrink-0">Failed TX</span>
                              <a
                                href={`${explorerBaseUrl}/tx/${txHash}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary hover:text-primary/80 font-mono truncate transition-colors"
                                title={txHash}
                              >
                                {txHash.slice(0, 10)}…{txHash.slice(-8)}
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {(approveStatus === "loading" || transferStatus === "loading" || isWaitingForPod) && !isFlowComplete && (
                    <Button
                      disabled
                      className="w-full bg-primary text-primary-foreground font-medium opacity-60 cursor-not-allowed"
                      style={{ height: "40px", borderRadius: "8px", fontSize: "14px" }}
                    >
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {processingButtonLabel}
                    </Button>
                  )}

                  {hasError && (
                    <Button
                      onClick={handleRetry}
                      className="w-full bg-muted hover:bg-foreground/10 text-foreground font-medium"
                      style={{ height: "40px", borderRadius: "8px", fontSize: "14px" }}
                    >
                      Try Again
                    </Button>
                  )}

                  {/* Final success + TX hash */}
                  {txHash && isFlowComplete && (
                    <div className="text-[13px] bg-muted/50 border border-border rounded-lg px-3 py-2">
                      <div className="text-muted-foreground mb-1">Transfer Completed</div>
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground shrink-0">TX</span>
                        <a
                          href={`${explorerBaseUrl}/tx/${txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80 font-mono truncate transition-colors"
                          title={txHash}
                        >
                          {txHash.slice(0, 10)}…{txHash.slice(-8)}
                        </a>
                      </div>
                    </div>
                  )}

                  {isFlowComplete && (
                    <Button
                      onClick={onClose}
                      className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium"
                      style={{ height: "40px", borderRadius: "8px", fontSize: "14px" }}
                    >
                      Done
                    </Button>
                  )}
                </div>
              )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AmountInputModal;
